import { createSlice, PayloadAction, Reducer } from "@reduxjs/toolkit";
import { UserState, ITicket, IUserInfo, GetUserOrgsPayload } from "./types";
import {
    getToken,
    getUserInfo,
    getUserOrgs,
    refreshToken,
    getDefaultUserOrg,
    exchangeCode
} from "./thunks";
import { OrganizationUserDefault } from "@switcherstudio/switcher-api-client";
import braze from "helpers/braze";

/**
 * Global window interface extension to include the Clarity identify API.
 *
 * @see https://learn.microsoft.com/en-us/clarity/setup-and-installation/identify-api#api-call
 * @see https://learn.microsoft.com/en-us/clarity/filters/custom-tags#step-2
 */
declare global {
    interface Window {
        clarity(
            action: "identify",
            customId: string,
            customSessionId?: string | null,
            customPageId?: string | null,
            friendlyName?: string
        ): void;
        clarity(action: "set", key: string, value: string): void;
    }
}

// TODO: configure these somewhere (store?)

const initialState = {
    ticket: undefined,
    userInfo: undefined,
    userStreamDestinations: [],
    orgStreamDestinations: [],
    orgMaxStreamDestinations: undefined,
    orgMaxUsers: undefined,
    defaultStreamDestination: undefined,
    orgName: undefined,
    orgs: [],
    userFeatures: [],
    defaultOrg: undefined,
    projectId: undefined,
    hasCompletedLivestream: false
} as UserState;

export const user = createSlice({
    name: "user",
    initialState: initialState,
    reducers: {
        logout: () => initialState,
        identify: (state, { payload }: PayloadAction<object>) => {
            analytics.identify(state.userInfo?.User?.Details?.Id || "", {
                email: state.userInfo?.User?.Details?.Email || "",
                username: state.userInfo?.User?.Details?.Email || "",
                // avatar: getGravatarImageUrl(),
                plan: state.ticket?.activePlan || "",
                enterprise: state.userInfo?.MetaData?.Details?.OrganizationId
                    ? true
                    : false,
                ...payload
            });

            braze.identify(state.userInfo?.User?.Details?.Id);
            braze.open();

            // identify the user in microsoft clarity
            if (typeof window.clarity === "function") {
                window.clarity(
                    "identify",
                    state.userInfo?.User?.Details?.Id,
                    null,
                    null,
                    state.userInfo?.User?.Details?.Id
                );
                window.clarity(
                    "set",
                    "email",
                    state.userInfo?.User?.Details?.Email
                );
                window.clarity(
                    "set",
                    "planId",
                    state.userInfo?.Plan?.Details?.Id
                );
                window.clarity(
                    "set",
                    "planName",
                    state.userInfo?.Plan?.Details?.Name
                );
            }

            try {
                profitwell("start", {
                    user_email: state.userInfo?.User?.Details?.Email
                });
            } catch (e) {
                // could not start profitwell
            }
        },
        setTicket: (state, { payload }: PayloadAction<ITicket>) => {
            state.ticket = payload;
        }
    },
    extraReducers: (builder) => {
        builder.addCase(
            getToken.fulfilled,
            (state, { payload }: PayloadAction<ITicket>) => {
                state.ticket = payload;
            }
        );

        builder.addCase(
            exchangeCode.fulfilled,
            (state, { payload }: PayloadAction<ITicket>) => {
                state.ticket = payload;
            }
        );

        builder.addCase(
            getUserInfo.fulfilled,
            (state, { payload }: PayloadAction<IUserInfo>) => {
                state.userInfo = payload;
            }
        );

        builder.addCase(
            getUserOrgs.fulfilled,
            (state, { payload }: PayloadAction<GetUserOrgsPayload>) => {
                const { orgs, maximumDestinations, maximumUsers, name } =
                    payload;
                state.orgs = orgs;
                state.orgMaxStreamDestinations = maximumDestinations;
                state.orgMaxUsers = maximumUsers;
                state.orgName = name;
            }
        );

        builder.addCase(
            refreshToken.fulfilled,
            (state, { payload }: PayloadAction<ITicket>) => {
                state.ticket = payload;
            }
        );

        builder.addCase(
            getDefaultUserOrg.fulfilled,
            (state, { payload }: PayloadAction<OrganizationUserDefault>) => {
                state.defaultOrg = payload;
            }
        );

        builder.addCase(getToken.rejected, (_, { error }) => {
            throw error;
        });

        builder.addCase(exchangeCode.rejected, (_, { error }) => {
            throw error;
        });

        builder.addCase(getUserInfo.rejected, (_, { error }) => {
            throw error;
        });

        builder.addCase(getUserOrgs.rejected, (_, { error }) => {
            throw error;
        });

        builder.addCase(refreshToken.rejected, (_, { error }) => {
            throw error;
        });

        builder.addCase(getDefaultUserOrg.rejected, (_, { error }) => {
            throw error;
        });
    }
});

export const { logout, identify, setTicket } = user.actions;

export default user.reducer as Reducer<UserState>;
